/**
 * Monoline SVG icons aus dem Redesign-Bundle. Stroke-based, inherit currentColor.
 *
 * Keine Lucide/Heroicons-Dependency — die Icons sind klein genug, dass eine
 * eigene Komponente günstiger ist als Tree-Shaking-Risiko.
 */

export type IconName =
  | "inbox"
  | "data"
  | "pulse"
  | "search"
  | "plus"
  | "refresh"
  | "back"
  | "play"
  | "check"
  | "chev"
  | "dot"
  | "cmd"
  | "user"
  | "logo"
  | "spark"
  | "download"
  | "layers";

interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
}

export function Icon({ name, size = 18, className }: IconProps) {
  const stroke = {
    width: size,
    height: size,
    stroke: "currentColor",
    fill: "none",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className,
  };

  switch (name) {
    case "inbox":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <path d="M3 13l3-8h12l3 8" />
          <path d="M3 13v6h18v-6" />
          <path d="M8 13a4 4 0 008 0" />
        </svg>
      );
    case "data":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <ellipse cx="12" cy="5" rx="8" ry="3" />
          <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
          <path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
        </svg>
      );
    case "pulse":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <path d="M3 12h4l2-6 4 12 2-6h6" />
        </svg>
      );
    case "search":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      );
    case "plus":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "refresh":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <path d="M3 12a9 9 0 0115.5-6.3L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 01-15.5 6.3L3 16" />
          <path d="M3 21v-5h5" />
        </svg>
      );
    case "back":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <path d="M15 6l-6 6 6 6" />
        </svg>
      );
    case "play":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <path d="M7 5l12 7-12 7z" />
        </svg>
      );
    case "check":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <path d="m4 12 5 5L20 6" />
        </svg>
      );
    case "chev":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <path d="m6 9 6 6 6-6" />
        </svg>
      );
    case "dot":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <circle cx="12" cy="12" r="3" fill="currentColor" />
        </svg>
      );
    case "cmd":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
        </svg>
      );
    case "user":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
        </svg>
      );
    case "logo":
      // Brand-Asset: bidi-icon.svg aus bidi-marketing/assets/logos/, in
      // public/ kopiert. Schwarzes B mit orangem Punkt — Brand-konform.
      return (
        <img
          src="/bidi-icon.svg"
          width={size}
          height={size}
          alt="Bidi"
          className={className}
          style={{ display: "block", borderRadius: 6 }}
        />
      );
    case "spark":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
        </svg>
      );
    case "download":
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <path d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
        </svg>
      );
    case "layers":
      // Set/Stückliste — gestapelte Lagen (Komponenten eines Set-Artikels).
      return (
        <svg viewBox="0 0 24 24" {...stroke}>
          <path d="M12 3l9 5-9 5-9-5 9-5z" />
          <path d="M3 13l9 5 9-5" />
        </svg>
      );
    default:
      return null;
  }
}
